/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { useSite, type SiteContextValue } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

/**
 * Safely reads the site context. Behaves like `useSite()` except that when
 * no SiteProvider is mounted (e.g. when this component is rendered under
 * MemoryRouter in tests or Storybook) it returns `null` instead of throwing.
 *
 * Named `use*` intentionally so React Hook lint rules recognise it as a
 * custom hook — `useSite()` is called on every render regardless of the
 * try/catch, so hook-order invariants still hold.
 */
function useTrySite(): SiteContextValue | null {
    try {
        return useSite();
    } catch {
        return null;
    }
}

/**
 * Returns the current site and locale identifiers for URL building.
 * Applies alias mappings when configured, falling back to the raw IDs.
 * Returns empty strings when called outside of a SiteProvider context.
 */
export function useCurrentSiteAndLocaleRef() {
    const siteContext = useTrySite();
    const site = siteContext?.site;
    const { i18n } = useTranslation();
    const config = useConfig();

    return {
        siteRef: (site?.alias ?? site?.id) as string,
        localeRef: config.localeAliasMap?.[i18n.language] ?? i18n.language,
    };
}
